import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ViewTypesConstants } from '@app/constants/viewTypes.constants';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { DbProgressiveDownloadService } from '@data/services/dbDownloadProgress.service';
import { ThematicBlocsService } from '@data/services/thematicBlocs.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageService } from '@shared/services/global-message.service';
import { Subject, Subscription, fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-download-progress',
  templateUrl: './download-progress.component.html',
  styleUrls: ['./download-progress.component.scss']
})
export class DownloadProgressComponent implements OnInit, OnDestroy {
  public isDownloadComplete = true;
  public progress = 100;
  public labels = {
    downloadingGuides: marker('common.downloadingGuides'),
    conecctionError: marker('errors.conecctionError')
  };
  public networkStatusOnline = true;
  public networkStatus$: Subscription = Subscription.EMPTY;
  public updatingContentInformation: {
    view: ViewTypesConstants;
    updates: { deleting: number; updating: number; adding: number };
  }[] = [];
  private setProgressTo = 100;
  constructor(
    private globalMessageService: GlobalMessageService,
    private translateService: TranslateService,
    private progressiveDownloadService: DbProgressiveDownloadService,
    private thematicBlocsService: ThematicBlocsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.checkNetworkStatus();
    this.progressiveDownloadService.progressSubject.pipe(untilDestroyed(this)).subscribe((progress: number) => {
      if (progress >= 100 && !this.isDownloadComplete) {
        setTimeout(() => (this.isDownloadComplete = true), (progress - this.setProgressTo) * 20);
      } else if (progress < 100) {
        this.isDownloadComplete = false;
      }
      this.setProgressTo = progress;
      this.setProgress();
    });
    this.progressiveDownloadService.checkDB();
    this.thematicBlocsService.checkDB();
    this.progressiveDownloadService.updatingContentInformation.pipe(untilDestroyed(this)).subscribe((updating) => {
      setTimeout(
        () => {
          this.updatingContentInformation = this.updatingContentInformation.filter((info) => info.view !== updating.view);
          if (updating.updates) {
            this.updatingContentInformation.push(updating);
          } else {
            this.refreshComponent();
          }
        },
        updating.updates ? 0 : 1000
      );
    });
  }

  ngOnDestroy(): void {}

  refreshComponent() {
    const currentUrl = this.router.url;

    if (currentUrl.includes('(cev:')) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(currentUrl);
      });
    }
  }

  public checkNetworkStatus() {
    this.networkStatusOnline = navigator.onLine;
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(
        untilDestroyed(this),
        map(() => navigator.onLine)
      )
      .subscribe((status) => {
        this.networkStatusOnline = status;
        this.isDownloadComplete = true;
      });
  }

  public showDownloadInformation(): void {
    const message = this.translateService.instant(marker('common.downloadingGuidesInfo'));
    this.globalMessageService.showSuccess({
      message,
      actionText: this.translateService.instant(marker('common.close')),
      duration: 15000
    });
  }

  public showUpdatingInformation(): void {
    this.globalMessageService.showSuccess({
      message: this.updatingMessage(true),
      actionText: this.translateService.instant(marker('common.close')),
      duration: 15000
    });
  }

  public updatingMessage(complete = false): string {
    const adding = this.updatingContentInformation.reduce((a, b) => a + b.updates.adding, 0);
    const updating = this.updatingContentInformation.reduce((a, b) => a + b.updates.updating, 0);
    const deleting = this.updatingContentInformation.reduce((a, b) => a + b.updates.deleting, 0);
    if (complete) {
      return this.translateService.instant(marker('common.updatingContentInfo'), { updating, adding, deleting });
    } else {
      return this.translateService.instant(marker('common.updatingContentInfoReducedInfo'), {
        total: updating + adding + deleting
      });
    }
  }

  private setProgress(): void {
    if (this.progress < this.setProgressTo || (this.progress === 100 && this.setProgressTo === 0)) {
      this.progress = this.progress >= 100 ? 0 : this.progress + 1;
      setTimeout(() => this.setProgress(), 10);
    }
  }
}
